import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { Settings } from "@desk/contracts";
import { useApi } from "./context";
import { describeError } from "./api";
import { qk } from "./cache";

type Group = "followUp" | "quietHours" | "notificationKinds";
const GROUPS: Group[] = ["followUp", "quietHours", "notificationKinds"];

/** PATCH /v1/settings body: top-level fields, and nested groups as partial objects. */
export type SettingsPatch = Partial<Omit<Settings, Group | "revision">> & { [G in Group]?: Partial<Settings[G]> };

/** Applies a patch the way the API does: nested groups merge field by field. */
export function mergeSettings(base: Settings, patch: SettingsPatch): Settings {
  const next = { ...base } as Record<string, unknown>;
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined) continue;
    next[key] = (GROUPS as string[]).includes(key) ? { ...(base[key as Group] as object), ...(value as object) } : value;
  }
  return next as Settings;
}

export interface SettingsSaverDeps {
  send(patch: SettingsPatch): Promise<Settings>;
  read(): Settings | undefined;
  write(s: Settings): void;
  onError?(err: unknown): void;
}

/**
 * Optimistic settings writes. Each patch shows at once (merged over what's on screen) and is sent
 * strictly in order, so the server applies choices in the order they were made. The screen shows
 * the latest server response with any still-pending patches on top, so an older response never
 * replaces a newer choice. A failed patch is dropped and the screen rolls back to the server state
 * plus the remaining pending patches.
 */
export class SettingsSaver {
  private pending: { id: number; patch: SettingsPatch }[] = [];
  private confirmed: Settings | undefined;
  private chain: Promise<void> = Promise.resolve();
  private nextId = 1;

  constructor(private readonly deps: SettingsSaverDeps) {}

  save(patch: SettingsPatch): Promise<void> {
    const current = this.deps.read();
    if (!current) return Promise.resolve();
    if (!this.pending.length) this.confirmed = current;
    const entry = { id: this.nextId++, patch };
    this.pending.push(entry);
    this.deps.write(mergeSettings(current, patch));
    const run = async () => {
      try {
        this.confirmed = await this.deps.send(patch);
        this.drop(entry.id);
      } catch (err) {
        this.drop(entry.id);
        this.deps.onError?.(err);
      }
      this.render();
    };
    this.chain = this.chain.then(run);
    return this.chain;
  }

  get busy(): boolean {
    return this.pending.length > 0;
  }

  private drop(id: number) {
    this.pending = this.pending.filter((p) => p.id !== id);
  }

  private render() {
    if (!this.confirmed) return;
    this.deps.write(this.pending.reduce((s, p) => mergeSettings(s, p.patch), this.confirmed));
  }
}

/** Settings writes for screens: optimistic, ordered, and rolled back with a toast on failure. */
export function useSaveSettings(): (patch: SettingsPatch) => Promise<void> {
  const api = useApi();
  const qc = useQueryClient();
  return useMemo(() => {
    const saver = new SettingsSaver({
      send: async (patch) => {
        await qc.cancelQueries({ queryKey: qk.settings });
        return api.updateSettings(patch);
      },
      read: () => qc.getQueryData<Settings>(qk.settings),
      write: (s) => qc.setQueryData(qk.settings, s),
      onError: (e) => toast.error("Couldn't save settings", { description: describeError(e) }),
    });
    return async (patch) => {
      await saver.save(patch);
      if (!saver.busy) void qc.invalidateQueries({ queryKey: qk.bootstrap });
    };
  }, [api, qc]);
}
